// src/app/auth/auth.service.ts
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Cookie } from 'ng2-cookies';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Globals } from '../globals';
import { Observable } from 'rxjs';
import { ConfirmService } from 'app/shared/confirm/confirm.service';
import { AppService } from 'app/app.service';
import translateJson from "translate.json";
import resourceUrlJson from "../../resourceURL.json";
import { UserIdleService } from 'angular-user-idle';

@Injectable()
export class AuthenticationService  {

    private username: string;
    private userrole: string;
    private fullname: string;
    private resourceURL: string;
    private expireDate: any;
    private rolesAndPermissions: boolean;
    private i18 = translateJson.i18;


    constructor(private router: Router, private http: HttpClient, private globals: Globals,
        private userIdle: UserIdleService) {
        this.username = globals.username;
        this.userrole = globals.userrole;
        this.rolesAndPermissions = globals.rolesAndPermissions;
        this.fullname = globals.fullname;
        this.setResourceURL();
    }

    helper = new JwtHelperService();

    /**
     * Set resource URL
     */
    setResourceURL() {
        if (window.location.origin == 'http://localhost:8085') {
            this.resourceURL = resourceUrlJson.settings.resourceURL;            
        } else {            
            this.resourceURL = window.location.origin;
        }
    }

    /**
     * Get resource URL
     */
    getResourceURL() {
        return this.resourceURL;
    }

    /**
     * Login function
     * @param userName user name from login form
     * @param password password from login form
     */
    login(userName: string, password: string) : Observable <any>{

        const url = this.getResourceURL() + "/api/v1/login";

        // Set request headers
        const requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8');

        // Set request parameters
        let requestParams = new HttpParams()
        .set('username', userName)
        .set('password', password);

        // Post login
        return this.http.post(url, requestParams, {headers: requestHeaders, responseType: 'json' });
    }

    /**
     * Ping server
     */
    getPingResponse() : Observable<any> {
        this.isAuthenticated();
        this.isTokenValid();
        const url = this.getResourceURL() + '/api/v1/health';
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
     * Get new token with refresh token
     */
    getNewToken() {
        if(this.isUserAuthenticated()){

            let requestParams = new HttpParams()
                .set('username', this.globals.username)
                .set('refresh_token', this.getRefreshToken());

            const requestHeaders = new HttpHeaders()
                .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
                .set('Authorization', 'Bearer ' + this.getAccessToken());

            this.http.post(this.getResourceURL() + "/api/v1/refresh/newToken", requestParams, {headers: requestHeaders, responseType: 'json' }).subscribe(
                data => {
                    //If reload successful update tokens
                    this.saveToken(data);               
                    return true;
                },
                error => {
                    this.logout();
                    this.showError(error)
                    return false;
                });
        }
    }


    /**
    * Getter for refreshToken
    */
    public getRefreshToken() {
        return Cookie.get(this.globals.clientId + '_refreshToken');
    }

    /**
    * Getter to fetch expire date for refresh token from the cookie
    */
    public getExpireDate() {
        return Cookie.get(this.globals.clientId + '_expireDate');
    }

    /**
    * Getter to fetch access token from the cookie
    */
    public getAccessToken() {
        return Cookie.get(this.globals.clientId + '_accessToken')
    }

    /**
    * Getter to fetch current user data from the cookie
    */
    public getCurrentUserToken() {
        return Cookie.get(this.globals.clientId + '_currentUser')
    }

    /**
     * Save token to cookie
     * @param token  JWT token
     */
    saveToken(token) {
        this.expireDate = null;
        if(token.expires_in != null) {
            this.expireDate = new Date().getTime() + (1000 * token.expires_in); // 600000 = 10 min, 60000
        } else {
            this.expireDate = new Date().getTime() + (1000 * 20);
        }

        Cookie.set(this.globals.clientId + '_accessToken', token.access_token, new Date(this.expireDate));
        Cookie.set(this.globals.clientId + '_refreshToken', token.refresh_token, new Date(this.expireDate));
        Cookie.set(this.globals.clientId + '_expireDate', this.expireDate);
        var user = {
            uid: token.uid,
            fullName: token.fullName,
            roles: token.roles
        };
        Cookie.set(this.globals.clientId + '_currentUser', JSON.stringify(user), new Date(this.expireDate));
        this.rolesAndPermissions = JSON.parse(token.rolesAndPermissions.toLowerCase());
        this.setDataFromCookie();
    }

    /**
     * Logout will delete cookies
     */
    logout() {
        this.deleteCookies();
        this.clearLocalStorage();
        this.router.navigate(['/login']);
    }

    /**
    * Delete cookies
    **/
    deleteCookies() {
        Cookie.delete(this.globals.clientId + '_accessToken');
        Cookie.delete(this.globals.clientId + '_refreshToken');
        Cookie.delete(this.globals.clientId + '_expireDate');
        Cookie.delete(this.globals.clientId + '_currentUser');
        this.globals.username = "";
        this.globals.userrole = "";
        this.globals.rolesAndPermissions = false;
        this.globals.fullname = "";
    }

    clearLocalStorage(){
        localStorage.clear();
    }

    /**
     * Check whether the token is expired
     */
    isAuthenticated() {
        if(this.checkClientIdExists()) {
            this.userIdle.resetTimer();
            this.getNewToken();
        } else {
            
        }

    }

    /**
     * Set the cookies
     */
    setDataFromCookie(){
        const token = this.getAccessToken();
        if (token.length > 0 && this.helper.isTokenExpired(token)) {
            this.logout();
        }
        this.username = this.getCurrentUserToken().split('"')[3];
        this.userrole = this.getCurrentUserToken().split('"')[11];
        this.fullname = this.getCurrentUserToken().split('"')[7];
        this.globals.username = this.username;
        this.globals.userrole = this.userrole;
        this.globals.rolesAndPermissions = this.rolesAndPermissions;
        this.globals.fullname = this.fullname != null && this.fullname.trim() !== '' ? this.fullname : this.username;
    }

    isUserAuthenticated() {
        const token = this.getAccessToken();
        if (token.length > 0 && !this.helper.isTokenExpired(token)) {
            return true;
        } else {
            return false;
        }
    }

    setRoute(route){
        this.router.navigate([route]);
    }
    
    /**
     * Setter for client id
     * @param data clientId object
     */
    setClientId(data) {
        this.globals.clientId = data.clientId;
    }

    /**
     * Getter for client id
     * @returns clientId string
     */
    getClientId(){
        return this.globals.clientId;
    }

    checkClientIdExists(){

        if(this.globals.clientId != undefined && this.globals.clientId != null && this.globals.clientId.length > 0) {
            return true;
        }
        return false;
    }

    checkAuthentication() {

        const url = this.getResourceURL() + "/api/v1/login/isAuthenticated";

        // Set request headers
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.getAccessToken());
        // Set request parameters
        let requestParams = new HttpParams()
        .set('accessToken', this.getAccessToken());

        // Post login
        return this.http.post(url, requestParams, {headers: requestHeaders, responseType: 'json' });
    }
    
    showError(error) {
        alert(this.getTranslatedErrorMessage(error));
    }
    
    
    getTranslatedErrorMessage(error: any): string {
        let errorMessage = error.split(":", 2); // Split by the first occurence of semicolonn

        if (errorMessage.length > 1) {
            let message = "ID: " + errorMessage[0] + ": ";
            for (let error of this.i18.errors) {
                for(var key in error) {
                    if (key === errorMessage[0]) {
                        return message += error[key];
                    }
                }    
            }
            return message += errorMessage[1];
        } else {
            return error;
        }        
    }

     isTokenValid(): boolean {
        let token = this.getAccessToken();
        if (token.length < 1 || (token.length > 0 && this.helper.isTokenExpired(token))) {
            this.logout();
            return false;
        } else {
            return true;
        }
    }
    
    /**
     * get clientId from backend
     */
   
    getClientIdFromSrv(){
        const url = this.getResourceURL() + "/api/v1/login/clientId";

        // Set request headers
        const requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8');

        // get request
        this.http.get(url, {headers: requestHeaders, responseType: 'json' }).subscribe(
            data => {
                this.setClientId(data);
            },
            error => {
                console.log(error)
            }
        );
    }

}
